import React from "react";
import { Form } from "react-bootstrap";
import { DISPLAY_LIMITS } from "./draftModel";
import { CheckField, Messages, Section, TextAreaField, TextField } from "./fields";

const PLACEHOLDER_HINT =
  "In the tagline and the hero, {tierName} shows the display name and {couponValue} shows this tier's coupon value for the rider's cycle (only on a tier with a coupon benefit). The hero may also use {coverYears}: the standard motor cover plus this tier's motor extension, in years (only on a tier with a motor extension).";
const BANNER_HINT =
  "The card a rider on a lower tier sees on Manage plan, inviting them to upgrade to this tier. All three may use {tierName}, {couponValue} and {coverYears}. The monthly subtitle may also use {extraPerMonth} (the new instalment less the rider's current one) and the one-time subtitle {upgradePrice} (the one-time upgrade price). Fill in all three, or leave all three blank for no card.";
const DESCRIPTION_HINT =
  "Separate paragraphs with a blank line. Start a line with '- ' for a bullet point. Wrap words in **bold** to make them bold.";

// What the Amigo app shows for this tier on the Extend warranty screens. Blank fields are sent as
// null and the app leaves them out.
const AppContentSection = ({ form, setForm, msgs, locked }) => {
  const display = form.display;
  const patch = (changes) => setForm((prev) => ({ ...prev, display: { ...prev.display, ...changes } }));
  const setLine = (index, value) =>
    setForm((prev) => ({
      ...prev,
      display: { ...prev.display, notCovered: prev.display.notCovered.map((line, i) => (i === index ? value : line)) },
    }));
  const removeLine = (index) =>
    setForm((prev) => ({
      ...prev,
      display: { ...prev.display, notCovered: prev.display.notCovered.filter((_, i) => i !== index) },
    }));
  const addLine = () =>
    setForm((prev) => ({ ...prev, display: { ...prev.display, notCovered: [...prev.display.notCovered, ""] } }));
  const heroTyped = display.heroTitle.trim() !== "" || display.heroSubtitle.trim() !== "";
  const bannerTyped = [display.bannerTitle, display.bannerSubtitleMonthly, display.bannerSubtitleOneTime].some(
    (value) => value.trim() !== ""
  );
  const atLimit = display.notCovered.length >= DISPLAY_LIMITS.notCoveredItems;

  return (
    <Section
      id="app"
      title="App content"
      description="What the Amigo app shows for this tier on the Extend warranty screens. Leave a field blank and the app leaves it out."
      messages={msgs("display")}
    >
      <p className="warranty-help">{PLACEHOLDER_HINT}</p>
      <div className="row">
        <div className="col-md-8">
          <TextField
            id="warranty-display-tagline"
            label="Tagline"
            value={display.tagline}
            maxLength={DISPLAY_LIMITS.tagline}
            disabled={locked}
            placeholder="for example 360° protection"
            messages={msgs("display.tagline")}
            onChange={(value) => patch({ tagline: value })}
          />
        </div>
        <div className="col-md-4 warranty-checks">
          <CheckField
            id="warranty-display-bestValue"
            label="Best value"
            checked={display.bestValue}
            disabled={locked}
            help="Only one tier in the market can carry the badge."
            messages={msgs("display.bestValue")}
            onChange={(value) => patch({ bestValue: value })}
          />
        </div>
      </div>

      <h6 className="warranty-subhead">Hero</h6>
      <Messages messages={msgs("display.hero")} />
      <div className="row">
        <div className="col-md-12">
          <TextField
            id="warranty-display-heroTitle"
            label="Hero title"
            value={display.heroTitle}
            maxLength={DISPLAY_LIMITS.heroTitle}
            disabled={locked}
            placeholder="for example Ride worry-free for 3 years with {tierName}"
            messages={msgs("display.hero.title")}
            onChange={(value) => patch({ heroTitle: value })}
          />
        </div>
        <div className="col-md-12">
          <TextField
            id="warranty-display-heroSubtitle"
            label="Hero subtitle"
            value={display.heroSubtitle}
            maxLength={DISPLAY_LIMITS.heroSubtitle}
            disabled={locked}
            placeholder="for example Save {couponValue} now"
            help="Fill in both, or leave both blank for no hero."
            messages={msgs("display.hero.subtitle")}
            onChange={(value) => patch({ heroSubtitle: value })}
          />
        </div>
        {heroTyped && (
          <div className="col-md-12 mb-3">
            <button
              type="button"
              className="warranty-inline-btn"
              disabled={locked}
              onClick={() => patch({ heroTitle: "", heroSubtitle: "" })}
            >
              Clear the hero
            </button>
          </div>
        )}
      </div>

      <h6 className="warranty-subhead">Upgrade banner</h6>
      <p className="warranty-help">{BANNER_HINT}</p>
      <Messages messages={msgs("display.upgradeBanner")} />
      <div className="row">
        <div className="col-md-12">
          <TextField
            id="warranty-display-bannerTitle"
            label="Banner title"
            value={display.bannerTitle}
            maxLength={DISPLAY_LIMITS.bannerTitle}
            disabled={locked}
            placeholder="for example Ride worry-free for {coverYears} years with {tierName}"
            messages={msgs("display.upgradeBanner.title")}
            onChange={(value) => patch({ bannerTitle: value })}
          />
        </div>
        <div className="col-md-12">
          <TextField
            id="warranty-display-bannerSubtitleMonthly"
            label="Subtitle for a monthly rider"
            value={display.bannerSubtitleMonthly}
            maxLength={DISPLAY_LIMITS.bannerSubtitle}
            disabled={locked}
            placeholder="for example Get 360° protection at just {extraPerMonth} more a month"
            messages={msgs("display.upgradeBanner.subtitleMonthly")}
            onChange={(value) => patch({ bannerSubtitleMonthly: value })}
          />
        </div>
        <div className="col-md-12">
          <TextField
            id="warranty-display-bannerSubtitleOneTime"
            label="Subtitle for a one-time rider"
            value={display.bannerSubtitleOneTime}
            maxLength={DISPLAY_LIMITS.bannerSubtitle}
            disabled={locked}
            placeholder="for example Get 360° protection at just {upgradePrice} more"
            messages={msgs("display.upgradeBanner.subtitleOneTime")}
            onChange={(value) => patch({ bannerSubtitleOneTime: value })}
          />
        </div>
        {bannerTyped && (
          <div className="col-md-12 mb-3">
            <button
              type="button"
              className="warranty-inline-btn"
              disabled={locked}
              onClick={() => patch({ bannerTitle: "", bannerSubtitleMonthly: "", bannerSubtitleOneTime: "" })}
            >
              Clear the banner
            </button>
          </div>
        )}
      </div>

      <h6 className="warranty-subhead">Plan details</h6>
      <Messages messages={msgs("display.details")} />
      <TextAreaField
        id="warranty-display-description"
        label="Description"
        value={display.description}
        maxLength={DISPLAY_LIMITS.description}
        disabled={locked}
        rows={6}
        help={DESCRIPTION_HINT}
        messages={msgs("display.details.description")}
        onChange={(value) => patch({ description: value })}
      />
      <Form.Group className="mb-3">
        <div className="form-label">Not covered</div>
        <Messages id="warranty-display-notCovered-msg" messages={msgs("display.details.notCovered")} />
        {display.notCovered.length === 0 && (
          <p className="warranty-help">Nothing listed. Add a line for each thing the plan does not cover.</p>
        )}
        {display.notCovered.map((line, index) => {
          const id = `warranty-display-notCovered-${index}`;
          const lineMsgs = msgs(`display.details.notCovered.${index}`);
          return (
            <div className="warranty-list-line" key={index}>
              <div className="warranty-list-line-row">
                <Form.Control
                  id={id}
                  type="text"
                  value={line}
                  maxLength={DISPLAY_LIMITS.notCoveredItem}
                  disabled={locked}
                  aria-label={`Not covered, line ${index + 1}`}
                  aria-invalid={lineMsgs.some((item) => item.severity === "error")}
                  aria-describedby={lineMsgs.length > 0 ? `${id}-msg` : undefined}
                  onChange={(event) => setLine(index, event.target.value)}
                />
                <button
                  type="button"
                  className="warranty-inline-btn is-danger"
                  disabled={locked}
                  aria-label={`Remove not covered line ${index + 1}`}
                  onClick={() => removeLine(index)}
                >
                  Remove
                </button>
              </div>
              <Messages id={`${id}-msg`} messages={lineMsgs} />
            </div>
          );
        })}
        <button type="button" className="warranty-inline-btn is-primary" disabled={locked || atLimit} onClick={addLine}>
          Add a line
        </button>
        {atLimit && <div className="warranty-help">At most {DISPLAY_LIMITS.notCoveredItems} lines.</div>}
      </Form.Group>

      <TextField
        id="warranty-display-termsUrl"
        label="Terms link"
        type="url"
        value={display.termsUrl}
        maxLength={DISPLAY_LIMITS.termsUrl}
        disabled={locked}
        placeholder="https://www.emotorad.com/terms"
        help="An https address. The app opens it from the plan details."
        messages={msgs("display.termsUrl")}
        onChange={(value) => patch({ termsUrl: value })}
      />
    </Section>
  );
};

export default AppContentSection;
